// React Query mutation hooks for modifying email data

import { useApiClient } from '@commander/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { EmailSendData, EmailUpdateData, ThreadAutoFollowUp } from '../../api/emailApi';
import { createEmailApi } from '../../api/emailApi';
import { communicationKeys } from '../communicationKeys';

/**
 * Hook to send a new email or save as draft with automatic cache invalidation
 */
export function useSendEmail() {
  const apiClient = useApiClient();
  const emailApi = createEmailApi(apiClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: EmailSendData) => emailApi.sendEmail(data),
    // Non-idempotent: auto-retry can deliver the email twice
    retry: 0,
    onSuccess: () => {
      // Invalidate and refetch email lists
      queryClient.invalidateQueries({ queryKey: communicationKeys.emails.lists() });
      // Invalidate unified inbox so new emails appear immediately
      queryClient.invalidateQueries({ queryKey: communicationKeys.unified.lists() });
    },
  });
}

/**
 * Hook to update an existing draft email with automatic cache invalidation
 */
export function useUpdateDraftEmail() {
  const apiClient = useApiClient();
  const emailApi = createEmailApi(apiClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: EmailUpdateData }) =>
      emailApi.updateDraftEmail(id, data),
    retry: 0,
    onSuccess: (_, variables) => {
      // Force immediate refetch of the updated draft (triggers GET request)
      // This ensures fresh data is in cache when user reopens the draft
      queryClient.refetchQueries({
        queryKey: communicationKeys.emails.detail(variables.id),
      });
      // Invalidate lists so they refetch when next viewed
      queryClient.invalidateQueries({ queryKey: communicationKeys.emails.lists() });
    },
  });
}

/**
 * Hook to star/unstar an email with automatic cache invalidation
 */
export function useStarEmail() {
  const apiClient = useApiClient();
  const emailApi = createEmailApi(apiClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isStarred }: { id: string; isStarred: boolean }) =>
      emailApi.starEmail(id, isStarred),
    onSuccess: () => {
      // Invalidate lists to show updated star status
      queryClient.invalidateQueries({ queryKey: communicationKeys.emails.lists() });
      // Also invalidate unified inbox to reflect star changes
      queryClient.invalidateQueries({ queryKey: communicationKeys.unified.lists() });
    },
  });
}

/**
 * Hook to mark email as read/unread with optimistic updates
 * Immediately updates UI while API call processes, with automatic rollback on error
 */
export function useMarkEmailRead() {
  const apiClient = useApiClient();
  const emailApi = createEmailApi(apiClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isRead }: { id: string; isRead: boolean }) =>
      emailApi.markEmailRead(id, isRead),
    onMutate: async ({ id, isRead }) => {
      // Cancel outgoing refetches ONLY for list queries (not threads or details)
      // This prevents canceling the thread panel fetch when marking as read
      await queryClient.cancelQueries({ queryKey: communicationKeys.emails.lists() });
      await queryClient.cancelQueries({ queryKey: communicationKeys.unified.lists() });

      // Snapshot previous values for rollback
      const previousEmailQueries = queryClient.getQueriesData({
        queryKey: communicationKeys.emails.lists(),
      });
      const previousUnifiedQueries = queryClient.getQueriesData({
        queryKey: communicationKeys.unified.lists(),
      });

      // Optimistically update all email list queries
      queryClient.setQueriesData({ queryKey: communicationKeys.emails.lists() }, (old: any) => {
        if (!old) return old;
        return {
          ...old,
          emails: old.emails.map((email: any) =>
            email.id === id
              ? { ...email, isRead, readAt: isRead ? new Date().toISOString() : null }
              : email
          ),
        };
      });

      // Optimistically update unified inbox queries
      queryClient.setQueriesData({ queryKey: communicationKeys.unified.lists() }, (old: any) => {
        if (!old) return old;
        return {
          ...old,
          items: old.items.map((item: any) =>
            item.type === 'email' && item.id === id
              ? { ...item, isRead, readAt: isRead ? new Date().toISOString() : null }
              : item
          ),
        };
      });

      return { previousEmailQueries, previousUnifiedQueries };
    },
    onError: (_err, _vars, context) => {
      // Rollback optimistic updates on error
      if (context?.previousEmailQueries) {
        context.previousEmailQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      if (context?.previousUnifiedQueries) {
        context.previousUnifiedQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      // Always refetch to ensure consistency with backend
      queryClient.invalidateQueries({ queryKey: communicationKeys.emails.lists() });
      queryClient.invalidateQueries({ queryKey: communicationKeys.unified.lists() });
      // Invalidate notifications to reflect backend marking them as read
      queryClient.invalidateQueries({ queryKey: communicationKeys.notifications.lists() });
      // Invalidate unread count to update badge
      queryClient.invalidateQueries({ queryKey: communicationKeys.notifications.unreadCount() });
    },
  });
}

/**
 * Hook to delete an email with automatic cache invalidation
 */
export function useDeleteEmail() {
  const apiClient = useApiClient();
  const emailApi = createEmailApi(apiClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => emailApi.deleteEmail(id),
    onSuccess: () => {
      // Invalidate lists to refetch without deleted email
      queryClient.invalidateQueries({ queryKey: communicationKeys.emails.lists() });
      // Invalidate unified inbox to reflect deletion
      queryClient.invalidateQueries({ queryKey: communicationKeys.unified.lists() });
      // Invalidate notifications to reflect deletion
      queryClient.invalidateQueries({ queryKey: communicationKeys.notifications.lists() });
      // Invalidate unread count to reflect deletion
      queryClient.invalidateQueries({ queryKey: communicationKeys.notifications.unreadCount() });
    },
  });
}

/**
 * Hook to bulk mark emails as read with optimistic updates
 * Immediately updates UI for all emails while API calls process
 */
export function useBulkMarkEmailsRead() {
  const apiClient = useApiClient();
  const emailApi = createEmailApi(apiClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ids, isRead }: { ids: string[]; isRead: boolean }) => {
      // Execute all mark read operations in parallel
      await Promise.all(ids.map((id) => emailApi.markEmailRead(id, isRead)));
    },
    onMutate: async ({ ids, isRead }) => {
      // Cancel outgoing refetches ONLY for list queries (not threads or details)
      // This prevents canceling the thread panel fetch when marking as read
      await queryClient.cancelQueries({ queryKey: communicationKeys.emails.lists() });
      await queryClient.cancelQueries({ queryKey: communicationKeys.unified.lists() });

      // Snapshot previous values for rollback
      const previousEmailQueries = queryClient.getQueriesData({
        queryKey: communicationKeys.emails.lists(),
      });
      const previousUnifiedQueries = queryClient.getQueriesData({
        queryKey: communicationKeys.unified.lists(),
      });

      // Optimistically update all email list queries
      queryClient.setQueriesData({ queryKey: communicationKeys.emails.lists() }, (old: any) => {
        if (!old) return old;
        return {
          ...old,
          emails: old.emails.map((email: any) =>
            ids.includes(email.id)
              ? { ...email, isRead, readAt: isRead ? new Date().toISOString() : null }
              : email
          ),
        };
      });

      // Optimistically update unified inbox queries
      queryClient.setQueriesData({ queryKey: communicationKeys.unified.lists() }, (old: any) => {
        if (!old) return old;
        return {
          ...old,
          items: old.items.map((item: any) =>
            item.type === 'email' && ids.includes(item.id)
              ? { ...item, isRead, readAt: isRead ? new Date().toISOString() : null }
              : item
          ),
        };
      });

      return { previousEmailQueries, previousUnifiedQueries };
    },
    onError: (_err, _vars, context) => {
      // Rollback optimistic updates on error
      if (context?.previousEmailQueries) {
        context.previousEmailQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      if (context?.previousUnifiedQueries) {
        context.previousUnifiedQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      // Always refetch to ensure consistency with backend
      queryClient.invalidateQueries({ queryKey: communicationKeys.emails.lists() });
      queryClient.invalidateQueries({ queryKey: communicationKeys.unified.lists() });
      // Invalidate notifications to reflect backend marking them as read
      queryClient.invalidateQueries({ queryKey: communicationKeys.notifications.lists() });
      // Invalidate unread count to update badge
      queryClient.invalidateQueries({ queryKey: communicationKeys.notifications.unreadCount() });
    },
  });
}

/**
 * Hook to bulk delete emails
 */
export function useBulkDeleteEmails() {
  const apiClient = useApiClient();
  const emailApi = createEmailApi(apiClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ids: string[]) => {
      // Execute all delete operations in parallel
      await Promise.all(ids.map((id) => emailApi.deleteEmail(id)));
    },
    onSuccess: () => {
      // Invalidate lists to refetch without deleted emails
      queryClient.invalidateQueries({ queryKey: communicationKeys.emails.lists() });
      // Invalidate unified inbox to reflect deletion
      queryClient.invalidateQueries({ queryKey: communicationKeys.unified.lists() });
      // Invalidate notifications to reflect deletion
      queryClient.invalidateQueries({ queryKey: communicationKeys.notifications.lists() });
      // Invalidate unread count to reflect deletion
      queryClient.invalidateQueries({ queryKey: communicationKeys.notifications.unreadCount() });
    },
  });
}

/**
 * Hook to turn a sent thread's auto follow-ups on or off. Flips the cached state immediately,
 * rolls back on error, and settles on the server's answer.
 */
export function useSetThreadAutoFollowUp() {
  const apiClient = useApiClient();
  const emailApi = createEmailApi(apiClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ emailId, enabled }: { emailId: string; enabled: boolean }) =>
      emailApi.setThreadAutoFollowUp(emailId, enabled),
    onMutate: async ({ emailId, enabled }) => {
      const queryKey = communicationKeys.emails.autoFollowUp(emailId);
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<ThreadAutoFollowUp>(queryKey);
      if (previous) {
        queryClient.setQueryData<ThreadAutoFollowUp>(queryKey, { ...previous, enabled });
      }
      return { previous };
    },
    onError: (_error, { emailId }, context) => {
      if (context?.previous) {
        queryClient.setQueryData(communicationKeys.emails.autoFollowUp(emailId), context.previous);
      }
      // The server refused the change, so re-read what it actually holds
      queryClient.invalidateQueries({ queryKey: communicationKeys.emails.autoFollowUp(emailId) });
    },
    // The response is the server's state after the change, so there is nothing to refetch
    onSuccess: (state, { emailId }) => {
      queryClient.setQueryData(communicationKeys.emails.autoFollowUp(emailId), state);
    },
  });
}
